import { Injectable } from '@angular/core';
import { SieveString } from '../../helpers/sieve/sieveString.class';
import { Observable } from 'rxjs/Observable';
import {
    IPataviumPagedResponse,
    IPataviumResponse,
    IPataviumResponseFirstItem,
    PataviumPagedResponseFirstItem
} from '../../helpers/response.class';
import { TableContractModel } from '../../models/request-and-responses/rest-api/classes/tableContractModel';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { IFormContractResponse } from '../../models/request-and-responses/rest-api/interfaces/formContractResponse';
import { IFormContractRequest } from '../../models/request-and-responses/rest-api/interfaces/formContractRequest';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EtagEntityType, EtagService } from '../etag.service';
import { ISendEmailResult } from '../../models/request-and-responses/rest-api/interfaces/sendEmailResult';
import { throwError } from 'rxjs';
import { ContentTypeSupported, FileDownloadService } from '../file-download.service';
import { ISendSmsForContractResponse } from '../../models/request-and-responses/rest-api/interfaces/sendSmsForContractResponse';
import { BufferedFile } from '../../models/request-and-responses/rest-api/classes/bufferedFile';
import { EnergyOrGasContract } from '../../models/request-and-responses/rest-api/enums/energyOrGasContract.enum';
import { TaskExportRequestSettings } from '../../models/request-and-responses/rest-api/classes/taskExportRequestSettings';
import { ITaskExportResponse } from '../../models/request-and-responses/rest-api/interfaces/taskExportResponse';
import { IContractExportCount } from '../../models/request-and-responses/rest-api/interfaces/contractExportCount';
import { PreScreening } from '../../models/request-and-responses/rest-api/classes/preScreening';
import { IPreScreeningRequest } from '../../models/request-and-responses/rest-api/interfaces/preScreeningRequest';
import { ICustomerAuditRequest, ICustomerAuditResponse } from '../../models/request-and-responses/rest-api/interfaces/customerAudit';

@Injectable()
export class ContractService {

    constructor(
        private http: HttpClient,
        private fileDownloadService: FileDownloadService) {
    }

    private static getFilenameFromContentDispositionHeader(headers: HttpHeaders) {
        try {
            const contentDispositionHeader = headers.get('Content-Disposition');
            let result = contentDispositionHeader
                .split(';')[1]
                .trim()
                .split('=')[1];
            result = result.replace(/"/g, '');
            return result;
        } catch (e) {
            console.error(
                `Error trying to get filename from Content-Disposition header. Null will be returned`,
                e
            );
            return null;
        }
    }

    getContractsForTable(sieveString: SieveString, column: string, columnVal: string): Observable<IPataviumPagedResponse<TableContractModel>> {

        if (column && columnVal) {

            return this.http
            .get<IPataviumPagedResponse<TableContractModel>>(
                environment.apiUrl + '/contracts/table?column=' + column + '&columnVal=' + columnVal + '&' + sieveString.toString().substring(1),
                {}
            )
            .pipe(
                map(response => {
                    response.payload.map(r => new TableContractModel(r));
                    return response;
                })
            );

        } else {

            return this.http
            .get<IPataviumPagedResponse<TableContractModel>>(
                environment.apiUrl + '/contracts/table' + sieveString.toString(),
                {}
            )
            .pipe(
                map(response => {
                    response.payload.map(r => new TableContractModel(r));
                    return response;
                })
            );

        }

    }

    getContract(id: number): Observable<IPataviumResponseFirstItem<IFormContractResponse>> {
        return this.http
            .get<IPataviumPagedResponse<IFormContractResponse>>(environment.apiUrl + '/contracts/' + id)
            .pipe(map(response => new PataviumPagedResponseFirstItem(response)));
    }

    createOrUpdateContractForTable(body: IFormContractRequest): Observable<IPataviumResponseFirstItem<TableContractModel>> {
        let httpHeaders = new HttpHeaders();
        const contractId = body.contract.id;
        if (contractId != null) {
            const recordEtag = EtagService.getEtagEntity(EtagEntityType.Contract, body.contract.id);
            httpHeaders = httpHeaders.set('If-Match', recordEtag.etag);
        }
        return this.http
            .put<IPataviumPagedResponse<TableContractModel>>(
                `${environment.apiUrl}/contracts/table`,
                body,
                { headers: httpHeaders }
            )
            .pipe(map(response => new PataviumPagedResponseFirstItem<TableContractModel>(response)));
    }

    removeContract(id: number): Observable<IPataviumResponse<number>> {
        return this.http
            .delete<IPataviumResponse<number>>(
                environment.apiUrl + '/contracts/' + id
            );
    }

    sendParcelToEsign(contract: IFormContractRequest): Observable<ISendEmailResult> {
        const contractId = contract.contract.id;
        if (contractId == null) {
            return throwError(`Prima di inviare il plico è necessario salvare il contratto`);
        }
        let httpHeaders = new HttpHeaders();
        const url = `${environment.apiUrl}/contracts/${contractId}/parcel/send-to-esign`;
        return this.http.post<{ payload: ISendEmailResult }>(url, contract, { headers: httpHeaders })
            .pipe(map(resp => resp.payload));
    }

    sendParcel(contract: IFormContractRequest): Observable<ISendEmailResult> {
        const contractId = contract.contract.id;
        if (contractId == null) {
            return throwError(`Prima di inviare il plico è necessario salvare il contratto`);
        }
        let httpHeaders = new HttpHeaders();
        const recordEtag = EtagService.getEtagEntity(EtagEntityType.Contract, contract.contract.id);
        httpHeaders = httpHeaders.append('If-Match', recordEtag.etag);
        const url = `${environment.apiUrl}/contracts/${contractId}/parcel/send-email`;
        return this.http.post<{ payload: ISendEmailResult }>(url, contract, { headers: httpHeaders })
            .pipe(map(resp => resp.payload));
    }

    downloadParcel(contract: IFormContractRequest): Observable<Promise<void>> {
        const contractId = contract.contract.id;
        if (contractId == null) {
            return throwError(`Prima di scaricare il plico è necessario salvare il contratto`);
        }
        let httpHeaders = new HttpHeaders();
        const recordEtag = EtagService.getEtagEntity(EtagEntityType.Contract, contract.contract.id);
        httpHeaders = httpHeaders.append('If-Match', recordEtag.etag);
        const url = `${environment.apiUrl}/contracts/${contractId}/parcel/download`;
        //todo: responseType: 'blob' and get contentType from resp
        //todo: get file name with a new api
        return this.http.post(url, contract, { headers: httpHeaders, responseType: 'arraybuffer' })
            .pipe(
                map(async resp => {
                    return await this.fileDownloadService.generateAndDownload(
                        resp,
                        ContentTypeSupported.Pdf,
                        `parcel_contract_${contractId}`,
                        true
                    );
                })
            );
    }

    sendSmsForContract(contract: IFormContractRequest): Observable<ISendSmsForContractResponse> {
        const contractId = contract.contract.id;
        if (contractId == null) {
            return throwError(`Prima di scaricare il plico è necessario salvare il contratto`);
        }
        let httpHeaders = new HttpHeaders();
        const recordEtag = EtagService.getEtagEntity(EtagEntityType.Contract, contract.contract.id);
        httpHeaders = httpHeaders.append('If-Match', recordEtag.etag);
        const url = `${environment.apiUrl}/contracts/${contractId}/sms/send`;
        return this.http.post<IPataviumPagedResponse<ISendSmsForContractResponse>>(url, contract, { headers: httpHeaders })
            .pipe(map(resp => resp.payload[0]));
    }

    /*// Non utilizzato: in attesa della ripresa della vendita a distanza
sendTelesellingMail(contractId: number): Observable<ISendEmailResult> {
    return this.http.post<IPataviumResponse<ISendEmailResult>>(`${environment.apiUrl}/contracts/${contractId}/teleselling-mail`, null)
        .pipe(map(r => r.payload[0]));
}*/

    exportContractCSV(sieveString: SieveString): Observable<BufferedFile> {
        const httpParams = new HttpParams({
            fromString: sieveString.withQuestionMark(false).toString()
        });
        const url = `${environment.apiUrl}/contracts/export`;
        return this.getCsvExportBufferedFile(httpParams, url);
    }

    getPhoneCallExportCSV(sieveString: SieveString): Observable<BufferedFile> {
        const httpParams = new HttpParams({
            fromString: sieveString.withQuestionMark(false).toString()
        });
        const url = `${environment.apiUrl}/contracts/export/phone-call`;
        return this.getCsvExportBufferedFile(httpParams, url);
    }

    private getCsvExportBufferedFile(httParams: HttpParams, url: string): Observable<BufferedFile> {
        return this.http
            .get<ArrayBuffer>(url, {
                params: httParams,
                observe: 'response',
                responseType: 'arraybuffer' as 'json'
            })
            .pipe(
                map(response => {
                    const buffer = response.body;
                    const mimeType = response.headers.get('Content-Type');
                    const name = ContractService.getFilenameFromContentDispositionHeader(
                        response.headers
                    );
                    return new BufferedFile(buffer, mimeType, name);
                })
            );
    }

    exportCSV(type: EnergyOrGasContract) {
        return this.http.get(environment.apiUrl + '/contracts/enerp/csv/export/' + type, {
            responseType: 'arraybuffer'
        });
    }

    sendExportCSV(type: EnergyOrGasContract) {
        return this.http.get(environment.apiUrl + '/contracts/enerp/export/' + type);
    }

    enqueueExportTask(settings: TaskExportRequestSettings) {
        //todo: l'informazione nella rotta è duplicata: refactoring della rotta
        return this.http.post<IPataviumPagedResponse<ITaskExportResponse>>(
            `${environment.apiUrl}/contracts/${settings.typeExport}/export`,
            settings,
            {
                reportProgress: true
            }
        );
    }

    countExportableContractsForSimpleExport(sieveString: SieveString): Observable<IPataviumResponseFirstItem<IContractExportCount>> {
        const params = new HttpParams({
            fromString: sieveString.withQuestionMark(false).toString()
        });
        return this.http
            .get<IPataviumPagedResponse<IContractExportCount>>(
                environment.apiUrl + '/contracts/export/count',
                {
                    params: params,
                    reportProgress: true
                }
            )
            .pipe(map(res => new PataviumPagedResponseFirstItem(res)));
    }

    countExportableContractsForPhoneCallExport(sieveString: SieveString): Observable<IPataviumResponseFirstItem<IContractExportCount>> {
        const params = new HttpParams({
            fromString: sieveString.withQuestionMark(false).toString()
        });
        return this.http
            .get<IPataviumPagedResponse<IContractExportCount>>(
                environment.apiUrl + '/contracts/export/phone-call/count',
                {
                    params: params,
                    reportProgress: true
                }
            )
            .pipe(map(res => new PataviumPagedResponseFirstItem(res)));
    }

    exportAttachments(type: EnergyOrGasContract) {
        return this.http.get(
            environment.apiUrl + '/contracts/enerp/zip/attachments/export/' + type,
            { responseType: 'arraybuffer' }
        );
    }

    customerAudit(body: ICustomerAuditRequest): Observable<IPataviumResponseFirstItem<ICustomerAuditResponse>> {
        let httpHeaders = new HttpHeaders();
        const contractId = body.contractId;
        const agentId = body.agentId;
        return this.http
            .post<IPataviumPagedResponse<ICustomerAuditResponse>>(
                `${environment.apiUrl}/contracts/pre-screening/${contractId}/${agentId}`,
                body,
                { headers: httpHeaders }
            )
            .pipe(map(response => new PataviumPagedResponseFirstItem<ICustomerAuditResponse>(response)));
    }

}



// WEBPACK FOOTER //
// ./src/app/services/resources/contract.service.ts